// UI orchestration for the email registration path (fingerprint pre-step,
// post-success side effects, error notification). API lifecycle lives in useRegister.
import { useRef, useState } from "react"

import { mapAuthError } from "@/features/v2/auth/register/actions/mapAuthError"
import { notifyRegisterError } from "@/features/v2/auth/register/actions/notifyRegisterError"
import { persistDeviceFingerprint } from "@/features/v2/auth/register/actions/persistDeviceFingerprint"
import { trackRegistrationStart } from "@/features/v2/auth/register/actions/trackRegistration"
import { useAuthStore } from "@/stores/userStore"

export type UseRegistrationParams = {
  welcomeBonus: string
  openOtpSheet: (email: string) => void
}

export const useRegistration = ({
  welcomeBonus,
  openOtpSheet
}: UseRegistrationParams) => {
  const hasStartedRegistration = useRef(false)
  const visitorIdRef = useRef<string | null>(null)
  const [isCollectingFingerprint, setIsCollectingFingerprint] = useState(false)

  // Collect visitorId for server-side blacklist check in lib/auth.ts hooks.before.
  // Persist happens after signup — /api/auth/fingerprint requires a session.
  const collectVisitorIdBeforeRegister = async (): Promise<string | null> => {
    setIsCollectingFingerprint(true)

    try {
      const { collectVisitorId } =
        await import("@/features/v2/auth/register/actions/collectVisitorId")
      const visitorId = await collectVisitorId()
      visitorIdRef.current = visitorId
      return visitorId
    } finally {
      setIsCollectingFingerprint(false)
    }
  }

  const handleRegistrationSuccessWithOtp = (userEmail: string) => {
    if (!hasStartedRegistration.current) {
      trackRegistrationStart()
      hasStartedRegistration.current = true
    }

    persistDeviceFingerprint({ visitorId: visitorIdRef.current })
    useAuthStore.getState().setTypeBonusSelected(welcomeBonus)
    openOtpSheet(userEmail)
  }

  const handleRegistrationError = (error: string, code?: string) => {
    notifyRegisterError(mapAuthError(error, code))
  }

  return {
    isCollectingFingerprint,
    collectVisitorIdBeforeRegister,
    handleRegistrationSuccessWithOtp,
    handleRegistrationError
  }
}
